
import React, { useState, useRef, useEffect } from 'react';
import { Project, LogEntry, AppTheme } from '../types';
import { getChatbotInsight } from '../services/geminiService';

interface ChatbotProps {
  project: Project;
  logs: LogEntry[];
  theme: AppTheme;
}

const Chatbot: React.FC<ChatbotProps> = ({ project, logs, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{role: 'user' | 'bot', text: string}[]>([
    { role: 'bot', text: "Ready to defend your margin. How can I help you audit project risk today?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const insight = await getChatbotInsight(userMsg, project, logs);
      setMessages(prev => [...prev, { role: 'bot', text: insight || "I couldn't generate an insight right now." }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'bot', text: "Error connecting to Intelligence Engine." }]);
    } finally {
      setLoading(false);
    }
  };

  const getBubbleStyle = () => {
    switch(theme) {
      case AppTheme.BAMBOO: return "bg-[#283618] text-[#fefae0]";
      case AppTheme.OCEAN: return "bg-[#003049] text-white";
      default: return "bg-black text-white";
    }
  };

  return (
    <div className="fixed bottom-12 right-12 z-50">
      {isOpen ? (
        <div className="w-96 bg-white rounded-[2.5rem] shadow-[0_20px_80px_rgba(0,0,0,0.15)] border border-slate-100 flex flex-col h-[500px] overflow-hidden animate-in slide-in-from-bottom-8 duration-500">
          <div className={`px-8 py-6 ${getBubbleStyle()} flex justify-between items-center transition-colors duration-500`}>
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] opacity-60">Intelligence Hub</p>
              <h3 className="text-sm font-bold tracking-tight">MarginGuard AI</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="opacity-40 hover:opacity-100 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-6">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-5 py-4 rounded-3xl text-xs leading-relaxed ${
                  m.role === 'user' ? getBubbleStyle() : 'bg-black/5 text-current font-medium'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-black/5 px-5 py-4 rounded-3xl flex gap-1">
                  <div className="w-1 h-1 bg-current opacity-40 rounded-full animate-bounce"></div>
                  <div className="w-1 h-1 bg-current opacity-40 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1 h-1 bg-current opacity-40 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 bg-white border-t border-slate-50 flex gap-2">
            <input 
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Ask for forensic advice..."
              className="flex-1 bg-black/5 border-none rounded-2xl px-5 py-3 text-xs focus:ring-1 focus:ring-black outline-none placeholder:text-slate-400"
            />
            <button 
              onClick={handleSend}
              className={`w-10 h-10 ${getBubbleStyle()} rounded-2xl flex items-center justify-center hover:scale-105 transition-all shadow-lg active:scale-95`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
            </button>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className={`w-16 h-16 ${getBubbleStyle()} rounded-[2rem] flex items-center justify-center shadow-[0_15px_40px_rgba(0,0,0,0.3)] hover:scale-110 hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)] transition-all duration-500 active:scale-90 relative group`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-4 border-[#f2f2f2] animate-pulse"></div>
          <span className={`${getBubbleStyle()} absolute right-20 text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl opacity-0 group-hover:opacity-100 transition-all pointer-events-none whitespace-nowrap shadow-xl`}>Forensic Insight</span>
        </button>
      )}
    </div>
  );
};

export default Chatbot;
